import {
  Alert,
  AlertTitle,
  Autocomplete,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { DateTime } from 'luxon';

// * Icons
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import RouterRoundedIcon from '@mui/icons-material/RouterRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';

// * Queries
import { useLazyGetSystemQoSByIdQuery } from '@/api/energy-analysis/systemQoSApiSlice';
import { useLazyGetBuildingByIdQuery } from '@/api/recalc/endpoints/buildingApiSlice';
import { useLazyGetDataQuery } from '@/api/recalc/endpoints/dataApiSlice';
import { useLazyGetGatewayByIdQuery } from '@/api/recalc/endpoints/gatewayApiSlice';
import { useLazyGetSubscriptionByIdQuery } from '@/api/recalc/endpoints/jobSubscriptionApiSlice';
import { useLazyGetSensorsByIdQuery } from '@/api/recalc/endpoints/sensorsApiSlice';
import { useLazyGetUnitByIdQuery } from '@/api/recalc/endpoints/unitsApiSlice';
import { useLazyGetEnergyGroupByIdQuery } from '@/api/recalcV2/endpoints/energyGroupsApiSlice';
import { useLazyGetEnergyTreeNodeByIdQuery } from '@/api/recalcV2/endpoints/energyTreeNodesApiSlice';
import {
  useLazyGetPowerLoadProfileAnalysisQuery,
  useLazyGetPowerLoadProfileAnalysisSeuQuery,
  useLazyGetEnergyConsumptionQuery,
} from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice';

import PowerLoadProfilesAnalysisChart from '@/components/highcharts/powerAnalyser/PowerLoadProfilesAnalysisChart';
import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper'; // Adjust the import path as necessary
import UnitAlarmChart from '../highcharts/UnitAlarmChart';
import DistributionAlarmBarChart from '../highcharts/DistributionAlarmBarChart';

const formatAndEncodeTimestampForAPI = (date) => {
  const isoString = DateTime.fromJSDate(date, { zone: 'utc' }).toFormat(
    "yyyy-MM-dd'T'HH:mm:ss'Z'",
  );
  return encodeURIComponent(isoString);
};

function formatDanishDate(timestamp) {
  if (!timestamp) return '';
  return DateTime.fromISO(timestamp, { zone: 'utc' })
    .setLocale('da')
    .toFormat('dd. MMM yyyy HH:mm:ss');
}

const toUTC = (timestamp) =>
  DateTime.fromISO(timestamp, { zone: 'utc' }).toMillis();

const SEVERITY_COLORS = {
  Critical: '#c72929',
  Major: '#c72929',
  Medium: '#f47f1f',
  Minor: '#ffb300',
  Trivial: '#4cae4f',
  Information: '#1975d2',
  Unknown: 'transparent',
};

const SEVERITY_BUTTON_CONFIG = {
  Critical: {
    label: 'Critical',
    icon: <ErrorRoundedIcon />,
    color: SEVERITY_COLORS.Critical,
  },
  Major: {
    label: 'Major',
    icon: <ErrorRoundedIcon />,
    color: SEVERITY_COLORS.Major,
  },
  Medium: {
    label: 'Medium',
    icon: <WarningRoundedIcon />,
    color: SEVERITY_COLORS.Medium,
  },
  Minor: {
    label: 'Minor',
    icon: <WarningRoundedIcon />,
    color: SEVERITY_COLORS.Minor,
  },
  Information: {
    label: 'Information',
    icon: <InfoRoundedIcon />,
    color: SEVERITY_COLORS.Information,
  },
  Trivial: {
    label: 'Trivial',
    icon: <InfoRoundedIcon />,
    color: SEVERITY_COLORS.Trivial,
  },
};

const getAlertSeverity = (severity) => {
  switch (severity) {
    case 'Critical':
      return 'error';
    case 'Major':
      return 'error';
    case 'Medium':
      return 'warning';
    case 'Minor':
      return 'warning';
    case 'Trivial':
      return 'success';
    case 'Information':
      return 'info';
    default:
      return 'default';
  }
};

const ViewAlarmDialog = ({ open, onClose, alarm }) => {
  const [associationContent, setAssociationContent] = useState(null);
  const [powerLoadProfileData, setPowerLoadProfileData] = useState(null);
  const [selectedEnergyTreeNode, setSelectedEnergyTreeNode] = useState(null);
  const [selectedSEUType, setSelectedSEUType] = useState(null);
  const [mode, setMode] = useState('node');
  const [seuOptions, setSeuOptions] = useState([]);
  const [distributionBoards, setDistributionBoards] = useState([]);
  const [distributionBoardData, setDistributionBoardData] = useState(null);
  const [alarmEnergyGroupId, setAlarmEnergyGroupId] = useState(null);

  const handleClose = () => {
    onClose();
    setAssociationContent(null);
    setPowerLoadProfileData(null);
    setSelectedEnergyTreeNode(null);
    setSelectedSEUType(null);
    setSeuOptions([]);
    setMode('node');
    setAlarmEnergyGroupId(null);
  };

  const [getUnitById] = useLazyGetUnitByIdQuery();
  const [getSensorsById] = useLazyGetSensorsByIdQuery();
  const [getSubscriptionById] = useLazyGetSubscriptionByIdQuery();
  const [getBuildingById] = useLazyGetBuildingByIdQuery();
  const [getGatewayById] = useLazyGetGatewayByIdQuery();
  const [getEnergyGroupById] = useLazyGetEnergyGroupByIdQuery();
  const [getEnergyTreeNodeById] = useLazyGetEnergyTreeNodeByIdQuery();
  const [getSystemQoSById] = useLazyGetSystemQoSByIdQuery();
  const [getPowerLoadProfileAnalysis] =
    useLazyGetPowerLoadProfileAnalysisQuery();
  const [getPowerLoadProfileAnalysisSeu] =
    useLazyGetPowerLoadProfileAnalysisSeuQuery();
  const [getEnergyConsumption] = useLazyGetEnergyConsumptionQuery();

  const [getData, { isLoading: isLoadingData, isFetching: isFetchingData }] =
    useLazyGetDataQuery();

  useEffect(() => {
    if (!alarm?.Associations) return;

    const {
      Units,
      Sensors,
      Buildings,
      JobSubscriptions,
      Gateways,
      EnergyGroups,
      EnergyTreeNodes,
    } = alarm.Associations;

    const fetchAssociations = async () => {
      try {
        if (Units.length > 0) {
          const units = await Promise.all(
            Units.map((id) => getUnitById(id).unwrap()),
          );

          if (alarm?.DataFrom && alarm?.DataTo) {
            const from = DateTime.fromISO(alarm?.DataFrom)
              .minus({ hours: 12 })
              .toMillis();
            const to = DateTime.fromISO(alarm?.DataTo)
              .plus({ hours: 12 })
              .toMillis();

            const seriesPerUnit = {};

            await Promise.all(
              units.map(async (unit) => {
                const sensorSeries = await Promise.all(
                  (unit.UnitSensors ?? []).map(async (unitSensor) => {
                    if (!unitSensor.Sensor || !unitSensor.UnitTypeInput) {
                      console.warn(
                        '❌ Skipping sensor due to missing fields:',
                        {
                          unitSensorId: unitSensor.UnitSensorId,
                          hasSensor: !!unitSensor.Sensor,
                          hasUnitTypeInput: !!unitSensor.UnitTypeInput,
                        },
                      );
                      return null;
                    }

                    try {
                      const fetchedData = await getData({
                        from: formatAndEncodeTimestampForAPI(new Date(from)),
                        to: formatAndEncodeTimestampForAPI(new Date(to)),
                        unitId: unit.UnitId,
                        datatype: 'temperature',
                        unitTypeInputId:
                          unitSensor.UnitTypeInput.UnitTypeInputId,
                        aggregateType: 'hour',
                      }).unwrap(); // 👈 unwrap the promise returned by the trigger

                      if (!fetchedData || fetchedData.length === 0) {
                        console.warn(
                          '⚠️ No data returned for',
                          unit.UnitId,
                          unitSensor.SensorId,
                        );
                        return null;
                      }

                      return {
                        name: `${unitSensor?.CustomName || 'Sensor'} (ID: ${unit.UnitId})`,
                        base: fetchedData[0]?.Base,
                        data: fetchedData.map((item) => [
                          toUTC(item.Timestamp),
                          parseFloat(item.Value.toFixed(2)),
                          item.Base,
                        ]),
                        type: 'spline',
                        showInNavigator: true,
                      };
                    } catch (e) {
                      console.error(
                        `❌ Failed fetching data for Unit ${unit.UnitId}, Sensor ${unitSensor.SensorId}`,
                        e,
                      );
                      return null;
                    }
                  }),
                );

                const filteredSeries = sensorSeries.filter(Boolean);
                seriesPerUnit[unit.UnitId] = filteredSeries;
              }),
            );

            const unitsWithData = units.map((unit) => ({
              ...unit,
              Data: seriesPerUnit[unit.UnitId] || [],
            }));

            setAssociationContent((prev) => ({
              ...prev,
              Units: unitsWithData,
            }));
          } else {
            setAssociationContent((prev) => ({
              ...prev,
              Units: units,
            }));
          }
        } else {
          setAssociationContent((prev) => ({
            ...prev,
            Units: [],
          }));
        }

        if (Sensors.length > 0) {
          const sensors = await Promise.all(
            Sensors.map((id) => getSensorsById(id).unwrap()),
          );
          setAssociationContent((prev) => ({
            ...prev,
            Sensors: sensors,
          }));
        } else {
          setAssociationContent((prev) => ({
            ...prev,
            Sensors: [],
          }));
        }

        if (Buildings.length > 0) {
          const buildings = await Promise.all(
            Buildings.map((id) => getBuildingById(id).unwrap()),
          );
          setAssociationContent((prev) => ({
            ...prev,
            Buildings: buildings,
          }));
        } else {
          setAssociationContent((prev) => ({
            ...prev,
            Buildings: [],
          }));
        }

        if (JobSubscriptions.length > 0) {
          const subscriptions = await Promise.all(
            JobSubscriptions.map((id) => getSubscriptionById({ id }).unwrap()),
          );
          setAssociationContent((prev) => ({
            ...prev,
            JobSubscriptions: subscriptions,
          }));
        } else {
          setAssociationContent((prev) => ({
            ...prev,
            JobSubscriptions: [],
          }));
        }

        if (Gateways.length > 0) {
          const gateways = await Promise.all(
            Gateways.map((id) => getGatewayById(id).unwrap()),
          );
          setAssociationContent((prev) => ({
            ...prev,
            Gateways: gateways,
          }));
        } else {
          setAssociationContent((prev) => ({
            ...prev,
            Gateways: [],
          }));
        }

        if (EnergyGroups.length > 0) {
          const energyGroups = await Promise.all(
            EnergyGroups.map((id) =>
              getEnergyGroupById({
                energyGroupId: id,
                accountId: alarm.AccountId,
              }).unwrap(),
            ),
          );

          setAssociationContent((prev) => ({
            ...prev,
            EnergyGroups: energyGroups,
          }));
        }

        if (EnergyTreeNodes.length > 0) {
          const energyTreeNode = await Promise.all(
            EnergyTreeNodes.map((node) =>
              getEnergyTreeNodeById({
                energyTreeNodeId: node,
                accountId: alarm.AccountId,
              }).unwrap(),
            ),
          );
          setAssociationContent((prev) => ({
            ...prev,
            EnergyTreeNodes: energyTreeNode,
          }));
        }
      } catch (error) {
        console.error('Error fetching associations:', error);
      }
    };

    const hasAnyAssociations =
      Units?.length > 0 ||
      Sensors?.length > 0 ||
      Buildings?.length > 0 ||
      JobSubscriptions?.length > 0 ||
      Gateways?.length > 0 ||
      EnergyGroups?.length > 0 ||
      EnergyTreeNodes?.length > 0;

    if (hasAnyAssociations) {
      fetchAssociations();
    }
  }, [
    alarm,
    getBuildingById,
    getData,
    getEnergyGroupById,
    getEnergyTreeNodeById,
    getGatewayById,
    getSensorsById,
    getSubscriptionById,
    getSystemQoSById,
    getUnitById,
  ]);

  useEffect(() => {
    if (!alarm || !alarm.DataFrom || !alarm.DataTo) return;
    if (!distributionBoards?.length) return;

    let cancelled = false;

    (async () => {
      try {
        const from = DateTime.fromISO(alarm.DataFrom)
          .minus({ hours: 12 })
          .toMillis();
        const to = DateTime.fromISO(alarm.DataTo)
          .plus({ hours: 12 })
          .toMillis();

        const results = await Promise.all(
          distributionBoards.map((boardId) =>
            getEnergyConsumption({
              accountId: alarm.AccountId,
              energyGroupId: alarmEnergyGroupId,
              nodeId: boardId,
              start: from,
              end: to,
              aggregate: 'day',
            }).unwrap(),
          ),
        );

        if (cancelled) return;
        setDistributionBoardData(results);
        // setState(results) if you need to keep them
      } catch (err) {
        if (!cancelled)
          console.error('Failed to fetch energy consumption:', err);
      }
    })();

    return () => {
      cancelled = true; // avoid setting state after unmount
    };
  }, [distributionBoards, alarmEnergyGroupId, getEnergyConsumption, alarm]);

  useEffect(() => {
    if (
      alarm?.DataFrom &&
      alarm?.DataTo &&
      mode === 'node' &&
      selectedEnergyTreeNode
    ) {
      const from = DateTime.fromISO(alarm.DataFrom)
        .minus({ hours: 12 })
        .toMillis();
      const to = DateTime.fromISO(alarm.DataTo).plus({ hours: 12 }).toMillis();
      getPowerLoadProfileAnalysis({
        nodeId: selectedEnergyTreeNode.energyTreeNodeId,
        energyGroupId: selectedEnergyTreeNode.energyGroupId,
        accountId: alarm.AccountId,
        startTimestamp: from,
        endTimestamp: to,
      })
        .unwrap()
        .then((data) => {
          setPowerLoadProfileData(data);
        })
        .catch((error) => {
          console.error('Error fetching Power Load Profile Analysis:', error);
        });
    } else if (
      alarm?.DataFrom &&
      alarm?.DataTo &&
      mode === 'seu' &&
      selectedSEUType
    ) {
      const from = DateTime.fromISO(alarm.DataFrom)
        .minus({ hours: 12 })
        .toMillis();
      const to = DateTime.fromISO(alarm.DataTo).plus({ hours: 12 }).toMillis();
      getPowerLoadProfileAnalysisSeu({
        seuType: selectedSEUType,
        energyGroupId: alarmEnergyGroupId,
        accountId: alarm.AccountId,
        startTimestamp: from,
        endTimestamp: to,
      })
        .unwrap()
        .then((data) => {
          setPowerLoadProfileData(data);
        })
        .catch((error) => {
          console.error(
            'Error fetching Power Load Profile Analysis for SEU:',
            error,
          );
        });
    }

    if (alarm && alarm?.MetaData)
      if (alarm?.MetaData) {
        try {
          const meta = JSON.parse(alarm.MetaData);

          const energyGroupId = meta.energy_tree_id;
          setAlarmEnergyGroupId(energyGroupId);

          const seuTypes =
            meta.alarm_details
              ?.filter(
                (detail) => detail.alarm_scope === 'SEU' && detail.seu_type,
              )
              .map((detail) => detail.seu_type) ?? [];

          const distributionBoards =
            meta.alarm_details
              ?.filter(
                (detail) =>
                  detail.alarm_scope === 'distribution_board' &&
                  detail.distribution_board_id,
              )
              .map((detail) => detail.distribution_board_id) ?? [];

          setDistributionBoards(distributionBoards);
          setSeuOptions(seuTypes);
        } catch (err) {
          console.error('Failed to parse alarm.MetaData:', err);
        }
      }
  }, [
    alarm,
    alarmEnergyGroupId,
    getPowerLoadProfileAnalysis,
    getPowerLoadProfileAnalysisSeu,
    mode,
    selectedEnergyTreeNode,
    selectedSEUType,
  ]);

  if (!alarm) return null;
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      backgroundColor="background.paper"
    >
      <DialogTitle gap={1} sx={{ display: 'flex', alignItems: 'center' }}>
        {alarm?.Title}
      </DialogTitle>
      <DialogContent dividers>
        <Alert
          variant="standard"
          severity={getAlertSeverity(alarm.Severity)}
          icon={SEVERITY_BUTTON_CONFIG[alarm.Severity]?.icon || null}
          sx={{
            borderLeft: '6px solid',
            borderColor: SEVERITY_COLORS[alarm.Severity] || 'divider',
            mb: 2,
          }}
        >
          <AlertTitle
            gap={1}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              {alarm?.AlarmType}
              <ArrowForwardRoundedIcon fontSize="small" /> {alarm?.AlarmSubtype}
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <AccessTimeRoundedIcon fontSize="small" />
              {formatDanishDate(alarm?.Created)}
            </Box>
          </AlertTitle>
          <Typography variant="caption" color="text.secondary">
            {alarm.Message ? `Message: ${alarm.Message}` : ''}
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            {alarm.Details ? `Details: ${alarm.Details}` : ''}
          </Typography>
        </Alert>
        <Stack display="flex" direction="row" gap={2} flexWrap="wrap" mb={2}>
          {/* Buildings */}
          {associationContent &&
            associationContent.Buildings?.map((building) => (
              <Card
                key={building.BuildingId}
                variant="outlined"
                flex={1}
                sx={{ minWidth: 350 }}
              >
                <CardContent>
                  <Stack
                    direction={'row'}
                    spacing={1}
                    alignItems="center"
                    mb={1}
                  >
                    <HomeWorkRoundedIcon />
                    <Typography variant="subtitle1">
                      {building.Name || 'Unknown Sensor Type'}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography variant="subtitle2" color="text.secondary">
                      Building ID: {building.BuildingId}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Address:{' '}
                      {building.Location.Address || 'Unknown Sensor Type'}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ))}

          {/* Job Subscriptions */}
          {associationContent &&
            associationContent.JobSubscriptions?.map((job) => (
              <Tooltip
                key={`job-tooltip-${job.JobSubscriptionId}`}
                title={job.JobType || 'No description available'}
              >
                <Card
                  key={job.BuildingId}
                  variant="outlined"
                  flex={1}
                  sx={{ minWidth: 350 }}
                >
                  <CardContent>
                    <Stack direction={'row'} spacing={1} mb={1}>
                      <ReceiptLongRoundedIcon />
                      <Typography variant="subtitle1">
                        {job.JobType || 'Unknown Sensor Type'}
                      </Typography>
                    </Stack>
                    <Stack>
                      <Typography variant="subtitle2" color="text.secondary">
                        JobSubscription ID: {job.JobSubscriptionId}
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        Name: {job.DisplayName}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Tooltip>
            ))}

          {/* Gateways */}
          {associationContent &&
            associationContent.Gateways?.map((gateway) => (
              <Card
                key={gateway.GatewayId}
                variant="outlined"
                flex={1}
                sx={{ minWidth: 350 }}
              >
                <CardContent>
                  <Stack direction={'row'} spacing={1} mb={1}>
                    <div
                      style={{
                        width: '25px',
                        height: '25px',
                        backgroundColor: '#323232',
                        borderRadius: '40%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          border: '1.5px solid #fff',
                          // border: '1.5px solid #AEF359',
                          borderRadius: '50%',
                        }}
                      ></div>
                    </div>
                    <Typography variant="subtitle1">
                      {gateway.Name || 'Unknown Sensor Type'}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography variant="subtitle2" color="text.secondary">
                      Gateway ID: {gateway.GatewayId}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Online: {gateway.Online ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Last Activity: {formatDanishDate(gateway.LastActivity)}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Current Application Firmware:{' '}
                      {gateway.CurrentApplicationFirmware}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ))}

          {/* Sensors */}
          {associationContent &&
            associationContent.Sensors?.map((sensor) => (
              <Card
                key={sensor.SensorId}
                variant="outlined"
                flex={1}
                sx={{ minWidth: 350 }}
              >
                <CardContent>
                  <Stack
                    direction={'row'}
                    spacing={1}
                    alignItems="center"
                    mb={1}
                  >
                    <SpeedRoundedIcon />
                    <Typography variant="subtitle1">
                      {sensor.SensorType || 'Unknown Sensor Type'}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography variant="subtitle2" color="text.secondary">
                      Sensor ID: {sensor.SensorId}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Data Last Recorded:{' '}
                      {formatDanishDate(sensor.DataLastRecorded) ||
                        'Unknown Sensor Type'}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Paired Gateway ID: {sensor.PairedGatewayId || 'N/A'}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Data Interval: {sensor.DataInterval}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Current Application Firmware:{' '}
                      {sensor.CurrentApplicationFirmware}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ))}

          {/* Units */}
          {associationContent &&
            associationContent.Units?.map((unit) => (
              <Card
                key={unit.UnitId}
                variant="outlined"
                flex={1}
                sx={{ minWidth: 350 }}
              >
                <CardContent>
                  <Stack direction={'row'} spacing={1} mb={1}>
                    <RouterRoundedIcon />
                    <Typography variant="subtitle1">
                      {unit.UnitType.Name || 'Unknown'}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography variant="subtitle2" color="text.secondary">
                      Unit ID: {unit.UnitId}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      Name: {unit.Name}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ))}

          {/* Units */}
          {associationContent &&
            associationContent.EnergyGroups?.map((group) => (
              <Card
                key={group.EnergyGroupId}
                variant="outlined"
                flex={1}
                sx={{ minWidth: 350 }}
              >
                <CardContent>
                  <Stack direction={'row'} spacing={1} mb={1}>
                    <RouterRoundedIcon />
                    <Typography variant="subtitle1">
                      {group.name || 'Unknown'}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography variant="subtitle2" color="text.secondary">
                      State: {group.options.state}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      {/* Name: {unit.Name} */}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ))}
        </Stack>
        <Box display="flex" alignItems="center" gap={2}>
          {mode === 'seu' &&
            seuOptions.length > 0 &&
            !alarm.AlarmSubtype.includes('Distribution') && (
              <Autocomplete
                fullWidth
                value={selectedSEUType}
                onChange={(_, newValue) => setSelectedSEUType(newValue)}
                options={seuOptions || []}
                // getOptionLabel={(node) => node.name || 'Unknown'}
                renderOption={(props, type) => (
                  <li {...props} key={type}>
                    <ListItemIcon>
                      <div
                        style={{
                          backgroundColor: getTypeColor(type),
                          height: 30,
                          width: 30,
                          borderRadius: '50%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          color: '#fff',
                        }}
                      >
                        <i className={`fa-solid ${getTypeIcon(type)}`}></i>
                      </div>
                    </ListItemIcon>
                    <ListItemText>{type || 'Unknown'}</ListItemText>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select SEU" />
                )}
              />
            )}

          {associationContent?.EnergyTreeNodes?.length > 0 &&
            !alarm.AlarmSubtype.includes('Distribution') &&
            mode === 'node' && (
              <Autocomplete
                fullWidth
                value={selectedEnergyTreeNode}
                onChange={(_, newValue) => setSelectedEnergyTreeNode(newValue)}
                options={associationContent.EnergyTreeNodes}
                getOptionLabel={(node) => node.name || 'Unknown'}
                renderOption={(props, node) => (
                  <li {...props} key={node.EnergyTreeNodeId}>
                    <ListItemIcon>
                      <div
                        style={{
                          backgroundColor: getTypeColor(node.type),
                          height: 30,
                          width: 30,
                          borderRadius: '50%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          color: '#fff',
                        }}
                      >
                        <i className={`fa-solid ${getTypeIcon(node.type)}`}></i>
                      </div>
                    </ListItemIcon>
                    <ListItemText>{node.name || 'Unknown'}</ListItemText>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Energy Tree Node"
                  />
                )}
              />
            )}
          {associationContent?.EnergyTreeNodes?.length > 0 &&
            !alarm.AlarmSubtype.includes('Distribution') && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setMode('node')}
              >
                Nodes
              </Button>
            )}

          {seuOptions.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setMode('seu')}
            >
              SEU
            </Button>
          )}
        </Box>

        {alarm?.DataFrom && alarm?.DataTo && (
          <Box display="flex" alignItems="center" gap={1} mb="1rem" mt="1rem">
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={<NotificationsRoundedIcon fontSize="small" />}
            >
              <AccessTimeRoundedIcon />
            </Badge>
            <Typography variant="subtitle1" color="text.secondary">
              Alarm Forekomst:
            </Typography>
            {formatDanishDate(alarm?.DataFrom)} -{' '}
            {formatDanishDate(alarm?.DataTo)}
          </Box>
        )}

        {!powerLoadProfileData &&
          !alarm.AlarmSubtype.includes('Distribution') &&
          !alarm.AlarmSubtype.includes('BaseloadDeviation') && (
            <UnitAlarmChart
              alarm={alarm}
              unitData={associationContent?.Units?.[0]?.Data || []}
              isLoading={isLoadingData || isFetchingData}
            />
          )}
        {alarm.AlarmSubtype.includes('BaseloadDeviation') &&
          powerLoadProfileData && (
            <PowerLoadProfilesAnalysisChart
              powerData={powerLoadProfileData.PowerData}
              loadData={powerLoadProfileData.PowerLoadProfileData}
              isLoading={false}
            />
          )}

        {/* {alarm.AlarmSubtype.includes('Distribution') && (
          <DistributionAlarmChart />
        )} */}

        <DistributionAlarmBarChart items={distributionBoardData || []} />

        {alarm?.MetaData && (
          <>
            <pre
              style={{
                marginTop: '1rem',
                padding: '1rem',
                backgroundColor: (theme) => theme.palette.background.paper,
                borderRadius: '4px',
                overflowX: 'auto',
              }}
            >
              {JSON.stringify(JSON.parse(alarm.MetaData), null, 2)}
            </pre>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewAlarmDialog;
